/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { AlertColor } from '@mui/material';
import { ModifyServicesContextType } from './ModifyServices.Model';

const ModifyServicesContext = createContext<ModifyServicesContextType>({} as ModifyServicesContextType);

interface ModifyServicesProviderProps {
  children: ReactNode;
}

export const ModifyServicesProvider: React.FC<ModifyServicesProviderProps> = ({ children }) => {
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);

  const [selectedTask, setSelectedTask] = useState('');
  const [expandedTask, setExpandedTask] = useState<string | null>(null);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);

  const [selectedSubTask, setSelectedSubTask] = useState('');
  const [subTask, setSubTask] = useState('');

  const [searchString, setSearchString] = useState('');
  const [searchSubTaskString, setSearchSubTaskString] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [isLoading, setIsLoading] = useState(false);
  const [isTasksLoading, setIsTasksLoading] = useState(false);
  const [isServicesLoading, setIsServicesLoading] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('success');

  const showSnackbar = (severity: AlertColor, message: string) => {
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const closeSnackbar = () => {
    setSnackbarOpen(false);
  };

  const value: ModifyServicesContextType = {
    selectedServiceId,
    setSelectedServiceId,

    selectedTask,
    setSelectedTask,
    expandedTask,
    setExpandedTask,
    openTaskId,
    setOpenTaskId,

    selectedSubTask,
    setSelectedSubTask,
    subTask,
    setSubTask,

    searchString,
    setSearchString,
    searchSubTaskString,
    setSearchSubTaskString,
    currentPage,
    setCurrentPage,

    isLoading,
    setIsLoading,
    isTasksLoading,
    setIsTasksLoading,
    isServicesLoading,
    setIsServicesLoading,

    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    showSnackbar,
    closeSnackbar,
  };

  return <ModifyServicesContext.Provider value={value}>{children}</ModifyServicesContext.Provider>;
};

export const useModifyServicesContext = (): ModifyServicesContextType => {
  const context = useContext(ModifyServicesContext);
  if (!context || Object.keys(context).length === 0) {
    throw new Error('useModifyServicesContext must be used within ModifyServicesProvider');
  }
  return context;
};
